import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import {
  Route,
  Routes,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";

import Topbar from "./pages/global/Topbar";
import Sidebar from "./pages/global/Sidebar";
import { ToastContainer } from "react-toastify";
import PrivateRoute from "./components/PrivateRoute";
import LoginPage from "./pages/auth/login";
import SignUpPage from "./pages/auth/SingUpPage";
import RemindPasswordPage from "./pages/auth/RemindPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import Dashboard from "./pages/Dashbboard";
import FinancialPage from "./pages/FinancialPage";
import AnnouncementPage from "./pages/AnnouncementPage";
import SystemSettingPage from "./pages/SystemSettingPage";
import WhatsAppManagePage from "./pages/WhatsAppManagePage";
import ReportPage from "./pages/ReportPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import TenantLandlordPage from "./pages/TenantLandlordPage";
import ServiceProviderDashboard from "./pages/ServiceProvider/dashboard";
import NotificationPage from "./pages/NotificationPage";
import { requestFCMToken } from "./services/firebaseConfig";
import React, { useEffect, useMemo } from "react";
import { AuthProvider } from "./provider/AuthProvider";
import { NotificationProvider } from "./provider/NotificationProvider";
import CMTDashboard from "./pages/CMTDashboard";
import Unauthorized from "./pages/UnauthorizedPage";
import PropertyManagementPage from "./pages/PropertyManagementPage";

import { getTheme } from "./theme";
import { useTranslation } from "react-i18next";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import i18n from "./i18n";

function App() {
  const { i18n } = useTranslation();
  const direction = i18n.language === "ar" ? "rtl" : "ltr";

  const [theme, colorMode] = useMode(direction);
  const location = useLocation();

  const cacheRtl = useMemo(
    () =>
      createCache({
        key: direction === "rtl" ? "muirtl" : "muiltr",
        stylisPlugins: direction === "rtl" ? [rtlPlugin] : [],
      }),
    [direction]
  );
  useEffect(() => {
    document.documentElement.lang = i18n.language || "en";
    document.body.dir = direction;
  }, [i18n.language, direction]);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
    .goog-te-banner-frame.skiptranslate,
    .goog-te-balloon-frame,
    .goog-logo-link,
    .goog-te-gadget,
    .skiptranslate {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    body {
      top: 0 !important;
    }
  `;
    document.head.appendChild(style);
  }, []);

  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (
      typeof args[0] === "string" &&
      args[0].includes("ResizeObserver loop completed")
    ) {
      return;
    }
    originalConsoleError(...args);
  };

  const authRoutes = [
    "/login",
    "/signup",
    "/remind-password",
    "/reset-password",
  ]; // ADD THIS
  const isAuthPage = authRoutes.some((route) =>
    location.pathname.startsWith(route)
  );
  const navigate = useNavigate();
  return (
    <ColorModeContext.Provider value={colorMode}>
      <AuthProvider>
        <NotificationProvider>
          <CacheProvider value={cacheRtl}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <ToastContainer
                position="top-right"
                autoClose={1000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />

              {isAuthPage ? (
                // === Only Login Page ===
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route
                    path="/remind-password"
                    element={<RemindPasswordPage />}
                  />
                  <Route
                    path="/reset-password/:token"
                    element={<ResetPasswordPage />}
                  />
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              ) : (
                // === Main Dashboard Layout ===
                <div className="app">
                  <Sidebar />
                  <main className="content">
                    <Topbar />
                    <div className="main-body">
                      <Routes>
                        <Route
                          path="/"
                          element={
                            <PrivateRoute>
                              <Dashboard />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/cmt"
                          element={
                            <PrivateRoute allowManagerOnly={true}>
                              <CMTDashboard />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/report"
                          element={
                            <PrivateRoute permission="canReport">
                              <ReportPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/tenant"
                          element={
                            <PrivateRoute permission="canAddTenantLandlord">
                              <TenantLandlordPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/serviceprovider"
                          element={
                            <PrivateRoute permission="canAddProvider">
                              <ServiceProviderDashboard />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/properties"
                          element={
                            <PrivateRoute permission="canAddProvider">
                              <PropertyManagementPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/financial"
                          element={
                            <PrivateRoute permission="canFinancial">
                              <FinancialPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/subscription"
                          element={
                            <PrivateRoute permission="canSubscription">
                              <SubscriptionPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/announcement"
                          element={
                            <PrivateRoute permission="canAnnouncement">
                              <AnnouncementPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/profile"
                          element={
                            <PrivateRoute>
                              <SystemSettingPage />
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/notification"
                          element={
                            <PrivateRoute>
                              <NotificationPage />
                            </PrivateRoute>
                          }
                        />
                        {/* <Route
                          path="/whatsapp"
                          element={
                            <PrivateRoute permission="canWhatsApp">
                              <WhatsAppManagePage />
                            </PrivateRoute>
                          }
                        /> */}
                        <Route
                          path="/unauthorized"
                          element={<Unauthorized />}
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </div>
                  </main>
                </div>
              )}
            </ThemeProvider>
          </CacheProvider>
        </NotificationProvider>
      </AuthProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
